import { FC, useState } from 'react';
import { Box, Button, Chip, Collapse, Divider, Fade, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SchoolIcon from '@mui/icons-material/School';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import RedeemIcon from '@mui/icons-material/Redeem';
import PlaceIcon from '@mui/icons-material/Place';
import LaunchIcon from '@mui/icons-material/Launch';
import PhoneIcon from '@mui/icons-material/Phone';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import useTranslations from '../global/translation/hooks/translation';
import { useThemeContext } from '../global/ThemeContext';
import promykSaleImg from '../../../app/media/promyk_sale.jpg';

const HORSE_LISTING_URL = 'https://re-volta.pl/8letni-walach-do-wprowadzenia-w-sport/o/690138/?zakres=5&zakres=6&4=1&wiekMin=1&wiekMax=26&sortowanie=0';

const Horses: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const [expanded, setExpanded] = useState(false);
  const [currentHorseIndex, setCurrentHorseIndex] = useState(0);

  const horses = [
    {
      id: 'promyk',
      image: promykSaleImg,
      discipline: 'horseDisciplineTag',
      title: 'horseSectionSubtitle',
      description: '2018 | 8-letni kasztanowaty wałach SP, elastyczny z zapasem mocy, starty do L (1.00 m), w domu skacze wyżej.',
      price: 'horsePriceValue',
      priceLabel: 'horsePriceLabel',
      url: HORSE_LISTING_URL,
      showDetails: true,
    },
    {
      id: 'comingSoon',
      image: null,
      discipline: 'horseDisciplineTag',
      title: 'horseSectionTitle',
      description: 'Więcej koni wkrótce / More horses coming soon',
      price: null,
      priceLabel: null,
      url: null,
      showDetails: false,
    },
  ];

  const currentHorse = horses[currentHorseIndex];

  const handleNextHorse = () => {
    setCurrentHorseIndex((prev) => (prev + 1) % horses.length);
    setExpanded(false);
  };

  const handlePrevHorse = () => {
    setCurrentHorseIndex((prev) => (prev - 1 + horses.length) % horses.length);
    setExpanded(false);
  };

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  const featureItems = [
    { key: 'horseSaleProfile', icon: <EmojiEventsIcon fontSize="small" /> },
    { key: 'horseSaleTemperament', icon: <DirectionsRunIcon fontSize="small" /> },
    { key: 'horseSaleTraining', icon: <SchoolIcon fontSize="small" /> },
    { key: 'horseSaleHealth', icon: <HealthAndSafetyIcon fontSize="small" /> },
    { key: 'horseSaleExtras', icon: <RedeemIcon fontSize="small" /> },
    { key: 'horseSaleLocation', icon: <PlaceIcon fontSize="small" /> },
  ] as const;

  const borderTone = darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(16, 35, 90, 0.06)';

  return (
    <Grid my={6} py={2} container justifyContent="center" alignItems="center" sx={{ bgcolor: darkMode ? '#0f0f15' : '#f7f9fc' }}>
      <Grid item xs={12} m={2} textAlign="center">
        <Typography variant="h4" fontWeight={300} sx={{ color: darkMode ? '#fff' : '#0f2340' }}>
          {translations.label.horseSectionTitle[currentLanguage]}
        </Typography>
      </Grid>

      <Grid item xs={12} md={10} p={2}>
        <Fade in timeout={600} key={currentHorseIndex}>
          <Paper
            elevation={16}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 3,
              background: darkMode
                ? 'linear-gradient(135deg, #0d1a30 0%, #10235a 45%, #0d1a30 100%)'
                : 'linear-gradient(135deg, #e8eef9 0%, #ffffff 48%, #dfe7f5 100%)',
              color: darkMode ? '#e8ecf5' : '#0f2340',
              boxShadow: darkMode ? '0 18px 40px rgba(0,0,0,0.35)' : '0 22px 55px rgba(16,35,90,0.18)',
              transition: 'transform 0.3s ease',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 240,
                right: -90,
                width: 240,
                height: 240,
                borderRadius: '50%',
                background: darkMode
                  ? 'radial-gradient(circle, rgba(61,104,199,0.35), rgba(13,26,48,0))'
                  : 'radial-gradient(circle, rgba(16,35,90,0.28), rgba(255,255,255,0))',
                filter: 'blur(2px)',
              }}
            />

            {currentHorse.image ? (
              <Box
                component="img"
                src={currentHorse.image}
                alt={`Horse ${currentHorseIndex}`}
                sx={{ width: '100%', height: 300, objectFit: 'cover', display: 'block', position: 'relative' }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: darkMode
                    ? 'linear-gradient(135deg, #0d1a30 0%, #10235a 45%, #0d1a30 100%)'
                    : 'linear-gradient(135deg, #e8eef9 0%, #ffffff 48%, #dfe7f5 100%)',
                  position: 'relative',
                }}
              >
                <Typography variant="h5" fontWeight={600} sx={{ color: darkMode ? '#a4c6ff' : theme.palette.primary.main }}>
                  Wkrótce / Coming Soon
                </Typography>
              </Box>
            )}

            <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, position: 'relative' }}>
              <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip
                  label={translations.label[currentHorse.discipline as keyof typeof translations.label][currentLanguage]}
                  color="primary"
                  size="small"
                  sx={{
                    bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(16,35,90,0.08)',
                    color: darkMode ? '#f5f7fb' : '#0f2340',
                    borderColor: borderTone,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    fontWeight: 600,
                  }}
                />
              </Stack>

              <Typography
                variant="h5"
                fontWeight={600}
                mb={3}
                sx={{
                  color: darkMode ? '#fff' : '#0f2340',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  wordBreak: 'break-word',
                }}
              >
                {translations.label[currentHorse.title as keyof typeof translations.label][currentLanguage]}
              </Typography>

              {currentHorse.price && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'stretch', sm: 'flex-end' }} mb={3}>
                  <Box sx={{ flex: { xs: 'none', sm: 1 } }}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: darkMode ? '#a4c6ff' : theme.palette.primary.main }}>
                      {translations.label[currentHorse.priceLabel as keyof typeof translations.label][currentLanguage]}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{
                        color: darkMode ? '#fff' : theme.palette.primary.main,
                        fontSize: { xs: '1.75rem', sm: '2.125rem' },
                      }}
                    >
                      {translations.label[currentHorse.price as keyof typeof translations.label][currentLanguage]}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ flex: { xs: 'none', sm: 1 } }}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      startIcon={<PhoneIcon />}
                      onClick={() => window.open('tel:729883891', '_self')}
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: '#fff',
                        fontWeight: 700,
                        boxShadow: darkMode ? '0 14px 28px rgba(0,0,0,0.35)' : '0 10px 24px rgba(16,35,90,0.25)',
                        '&:hover': { bgcolor: theme.palette.primary.dark, transform: 'translateY(-1px)' },
                      }}
                    >
                      {translations.label.horseCTACall[currentLanguage]}
                    </Button>
                    <Button
                      variant={darkMode ? 'outlined' : 'text'}
                      size="small"
                      fullWidth
                      startIcon={<LaunchIcon />}
                      onClick={() => window.open(currentHorse.url || '', '_blank')}
                      sx={{
                        color: darkMode ? '#e8ecf5' : '#0f2340',
                        borderColor: borderTone,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(16,35,90,0.06)',
                          borderColor: darkMode ? '#4f7edb' : '#10235a',
                        },
                      }}
                    >
                      {translations.label.horseCTAListing[currentLanguage]}
                    </Button>
                  </Stack>
                </Stack>
              )}

              {currentHorse.showDetails && (
                <>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 3, borderColor: borderTone }} />
                    <Grid container spacing={3} mb={3}>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1.5}>
                          {featureItems.map(({ key, icon }) => (
                            <Stack key={key} direction="row" spacing={1.5} alignItems="flex-start">
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '12px',
                                  display: 'grid',
                                  placeItems: 'center',
                                  bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(16,35,90,0.08)',
                                  color: darkMode ? '#a4c6ff' : theme.palette.primary.main,
                                  border: `1px solid ${borderTone}`,
                                }}
                              >
                                {icon}
                              </Box>
                              <Typography variant="body1" sx={{ color: darkMode ? '#dfe6f3' : '#1d2d4f' }}>
                                {translations.label[key as keyof typeof translations.label][currentLanguage]}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Stack spacing={2}>
                          <Grid container spacing={1}>
                            {[
                              {
                                label: translations.label.horseSex[currentLanguage],
                                value: translations.label.horseSexGelding[currentLanguage],
                              },
                              {
                                label: translations.label.horseBirthDate[currentLanguage],
                                value: translations.label.horseBirthYear[currentLanguage],
                              },
                              {
                                label: translations.label.horsePurpose[currentLanguage],
                                value: translations.label.horseJumping[currentLanguage],
                              },
                              {
                                label: translations.label.horseCoat[currentLanguage],
                                value: translations.label.horseChestnut[currentLanguage],
                              },
                              {
                                label: translations.label.horseBreed[currentLanguage],
                                value: translations.label.horseBreedType[currentLanguage],
                              },
                            ].map((item, idx) => (
                              <Grid key={idx} item xs={12}>
                                <Stack direction="row" spacing={1}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ color: darkMode ? '#a4c6ff' : theme.palette.primary.main, minWidth: '110px' }}
                                  >
                                    {item.label}:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: darkMode ? '#dfe6f3' : '#1d2d4f' }}>
                                    {item.value}
                                  </Typography>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color: darkMode ? '#a4c6ff' : theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: 0.5 }}
                            >
                              {translations.label.horseCharacterTitle[currentLanguage]}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                              {[
                                translations.label.horseProfessor[currentLanguage],
                                translations.label.horseBeginners[currentLanguage],
                                translations.label.horseExperienced[currentLanguage],
                                translations.label.horseTerrain[currentLanguage],
                                translations.label.horseDogFriendly[currentLanguage],
                              ].map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: borderTone, color: darkMode ? '#dfe6f3' : '#1d2d4f', fontSize: '0.75rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color: darkMode ? '#a4c6ff' : theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: 0.5 }}
                            >
                              {translations.label.horseCompetitionTitle[currentLanguage]}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                              {[translations.label.horseSocial[currentLanguage], translations.label.horseRegional[currentLanguage]].map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: borderTone, color: darkMode ? '#dfe6f3' : '#1d2d4f', fontSize: '0.75rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color: darkMode ? '#a4c6ff' : theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: 0.5 }}
                            >
                              {translations.label.horseFeaturesTitle[currentLanguage]}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                              {[
                                translations.label.horseTransport[currentLanguage],
                                translations.label.horseFarrier[currentLanguage],
                                translations.label.horseHooves[currentLanguage],
                                translations.label.horseGroundwork[currentLanguage],
                                translations.label.horseWithEquipment[currentLanguage],
                                translations.label.horsePhysiotherapy[currentLanguage],
                              ].map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: borderTone, color: darkMode ? '#dfe6f3' : '#1d2d4f', fontSize: '0.75rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Collapse>

                  <Button
                    onClick={handleExpandClick}
                    size="small"
                    sx={{
                      color: darkMode ? '#a4c6ff' : theme.palette.primary.main,
                      textTransform: 'none',
                      fontWeight: 600,
                      mt: 2,
                      '&:hover': { backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(16,35,90,0.05)' },
                    }}
                  >
                    {expanded ? translations.typography.readLess[currentLanguage] : translations.typography.readMore[currentLanguage]}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Fade>

        {/* Navigation Arrows */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
          <IconButton
            onClick={handlePrevHorse}
            sx={{
              color: darkMode ? '#a4c6ff' : theme.palette.primary.main,
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(16,35,90,0.08)',
              },
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Stack direction="row" spacing={1} justifyContent="center" flex={1}>
            {horses.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => {
                  setCurrentHorseIndex(idx);
                  setExpanded(false);
                }}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: idx === currentHorseIndex ? theme.palette.primary.main : borderTone,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: idx === currentHorseIndex ? theme.palette.primary.main : darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(16,35,90,0.12)',
                  },
                }}
              />
            ))}
          </Stack>
          <IconButton
            onClick={handleNextHorse}
            sx={{
              color: darkMode ? '#a4c6ff' : theme.palette.primary.main,
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(16,35,90,0.08)',
              },
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Horses;
